import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, Loader2, AlertTriangle, ShieldAlert, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export function InviteResetButton({ invite, onDone }) {
  const [auditData, setAuditData] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const hasActivity = invite.first_scan_at || invite.lead_created_at || invite.eligibility_answer;
  if (!hasActivity) return null;

  const open = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/property-campaign-invites/${invite.id}/reset-audit`);
      setAuditData(data); setConfirmText("");
    } catch (e) { toast.error(e.response?.data?.detail || "Audit failed"); }
    finally { setBusy(false); }
  };

  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/property-campaign-invites/${invite.id}/reset-test-activity`, { confirm_text: confirmText });
      toast.success(`Test activity removed — recipient restored to "${data.restored_status.replaceAll("_", " ")}"`);
      setAuditData(null); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button disabled={busy} onClick={open} data-testid={`pc-invite-reset-${invite.id}`}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-900 rounded-full border border-amber-300 bg-amber-50 px-3 py-1.5">
        <RotateCcw className="w-3.5 h-3.5" /> Reset Test Activity
      </button>
      {auditData && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setAuditData(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="invite-reset-dialog">
            <h3 className="font-bold text-slate-900 text-lg">Reset Test Activity — Dry Run</h3>
            <p className="text-xs text-slate-500 mt-1">Removes ONLY accidental test interactions for <b>{auditData.address}</b>. Nothing has been removed yet.</p>
            <div className="mt-3 space-y-1" data-testid="invite-reset-removable">
              {auditData.removable.map((r, i) => (
                <div key={i} className="flex justify-between text-sm rounded-lg bg-red-50 border border-red-200 px-3 py-1.5">
                  <span>{r.label}</span><span className="font-semibold">{r.count}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900 space-y-0.5" data-testid="invite-reset-preserved">
              <div>Campaign: <b className="font-mono">{auditData.preserved.campaign_assignment || "—"}</b> · QR token: <b className="font-mono">{invite.qr_token}</b> — QR stays valid for the real resident</div>
              <div>Mailed status, letter ref, Bing Mail history and the original recipient record are untouched.</div>
            </div>
            <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600" data-testid="invite-reset-canonical-note">
              {auditData.canonical_note}
            </div>
            {auditData.warnings.length > 0 && (
              <div className="mt-2 rounded-lg bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900">
                {auditData.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
              </div>
            )}
            {auditData.blockers.length > 0 ? (
              <div className="mt-3 rounded-lg bg-red-100 border border-red-400 px-3 py-2" data-testid="invite-reset-blockers">
                <div className="text-sm font-bold text-red-800 flex items-center gap-1.5"><ShieldAlert className="w-4 h-4" /> Reset blocked — shared live records</div>
                <ul className="mt-1 text-xs text-red-800 list-disc pl-4">{auditData.blockers.map((b, i) => <li key={i}>{b.detail}</li>)}</ul>
              </div>
            ) : (
              <div className="mt-3">
                <div className="text-xs text-slate-600">Recipient returns to <b>{auditData.restored_status.replaceAll("_", " ")} / Not Scanned</b>. Type <b className="font-mono">{auditData.confirm_phrase}</b> to confirm:</div>
                <input className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)} placeholder={auditData.confirm_phrase} data-testid="invite-reset-confirm-input" />
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setAuditData(null)}>Cancel</Button>
              {auditData.resettable && (
                <Button disabled={busy || confirmText !== auditData.confirm_phrase} onClick={doReset}
                  className="rounded-full bg-amber-600 hover:bg-amber-700" data-testid="invite-reset-confirm-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Remove Test Activity"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function RenameCampaignButton({ campaignId, onRenamed }) {
  const [info, setInfo] = useState(null);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const open = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/property-campaigns/${campaignId}/rename-info`);
      setInfo(data); setNewName(data.current_name);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
    finally { setBusy(false); }
  };

  const rename = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/property-campaigns/${campaignId}/rename`, { new_name: newName, confirm: true });
      toast.success(`Renamed to "${data.new_name}" — code ${data.code_unchanged} and all IDs unchanged`);
      setInfo(null); onRenamed?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Rename failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" disabled={busy} onClick={open} className="rounded-full" data-testid="pc-rename-btn">
        <Pencil className="w-4 h-4 mr-1" /> Rename
      </Button>
      {info && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setInfo(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="pc-rename-dialog">
            <h3 className="font-bold text-slate-900 text-lg">Rename Campaign</h3>
            <p className="text-xs text-slate-500 mt-1">Changes the display name ONLY. Campaign ID, code, QR tokens, letter refs, mail history, scans, leads, appointments, inspections, reports and analytics are all untouched.</p>
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm space-y-1">
              <div>Current name: <b>{info.current_name}</b></div>
              <div>Campaign ID: <span className="font-mono text-xs">{info.campaign_id}</span></div>
              <div>Campaign code: <span className="font-mono text-xs font-semibold">{info.campaign_code}</span> (unchanged)</div>
              <div>{info.properties} properties · {info.leads} leads · {info.mailed_count} mailed</div>
            </div>
            {info.warning && (
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900" data-testid="pc-rename-mailed-warning">
                <AlertTriangle className="inline w-3.5 h-3.5 mr-1" /> {info.warning}
              </div>
            )}
            <input className="mt-4 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" value={newName}
              onChange={(e) => setNewName(e.target.value)} placeholder="New display name" data-testid="pc-rename-input" />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setInfo(null)}>Cancel</Button>
              <Button disabled={busy || newName.trim().length < 3 || newName === info.current_name} onClick={rename}
                className="rounded-full bg-slate-900" data-testid="pc-rename-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Rename"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function SafeDeleteCampaignButton({ campaignId }) {
  const [auditData, setAuditData] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const open = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/property-campaigns/${campaignId}/delete-audit`);
      setAuditData(data); setConfirmText("");
    } catch (e) { toast.error(e.response?.data?.detail || "Audit failed"); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/property-campaigns/${campaignId}/safe-delete`, { confirm_text: confirmText });
      toast.success("Campaign deleted (campaign-specific records only)");
      navigate("/admin/property-campaigns");
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" disabled={busy} onClick={open} className="rounded-full border-red-300 text-red-700 hover:bg-red-50" data-testid="pc-safe-delete-btn">
        <Trash2 className="w-4 h-4 mr-1" /> Safe Delete
      </Button>
      {auditData && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setAuditData(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="pc-delete-dialog">
            <h3 className="font-bold text-slate-900 text-lg">Safe Delete — Dependency Audit</h3>
            <p className="text-xs text-slate-500 mt-1">Dry run of everything connected to <b>{auditData.campaign_name}</b> ({auditData.campaign_code}). Nothing has been deleted yet.</p>

            <div className="mt-4 space-y-1.5" data-testid="pc-delete-deps">
              {auditData.dependencies.map((d) => (
                <div key={d.collection} className="flex justify-between text-sm rounded-lg bg-slate-50 border border-slate-200 px-3 py-1.5">
                  <span>{d.label}</span>
                  <span className="font-semibold">{d.count} <span className="text-[10px] text-slate-400 uppercase">will be deleted</span></span>
                </div>
              ))}
              {!auditData.dependencies.length && <div className="text-sm text-slate-500">No linked records — empty campaign.</div>}
              {auditData.master_property_links > 0 && (
                <div className="flex justify-between text-sm rounded-lg bg-cyan-50 border border-cyan-200 px-3 py-1.5">
                  <span>Master Address Database records</span>
                  <span className="font-semibold text-cyan-800">{auditData.master_property_links} <span className="text-[10px] uppercase">preserved (link cleared)</span></span>
                </div>
              )}
            </div>

            {auditData.blockers.length > 0 ? (
              <div className="mt-4 rounded-xl bg-red-50 border border-red-300 p-3" data-testid="pc-delete-blockers">
                <div className="text-sm font-bold text-red-800 flex items-center gap-1.5"><ShieldAlert className="w-4 h-4" /> Deletion blocked — shared live records found</div>
                <ul className="mt-2 space-y-1 text-xs text-red-800 list-disc pl-4">
                  {auditData.blockers.map((b, i) => <li key={i}>{b.detail}</li>)}
                </ul>
                <div className="text-[11px] text-red-700 mt-2">Live customers, bookings, live mail history and redeemed offers are never deleted. Unlink or resolve these first.</div>
              </div>
            ) : (
              <div className="mt-4">
                <div className="text-xs text-slate-600">Type the campaign code <b className="font-mono">{auditData.confirm_phrase}</b> to permanently delete:</div>
                <input className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono" value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)} placeholder={auditData.confirm_phrase} data-testid="pc-delete-confirm-input" />
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setAuditData(null)}>Cancel</Button>
              {auditData.deletable && (
                <Button disabled={busy || confirmText !== auditData.confirm_phrase} onClick={doDelete}
                  className="rounded-full bg-red-600 hover:bg-red-700" data-testid="pc-delete-confirm-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Permanently Delete"}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
